'use strict';

import React, {
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef
} from 'react';
import {
    useDispatch,
    useSelector
} from 'react-redux';
import {keys} from 'ts-transformer-keys';

import {Card} from 'primereact/card';

import {
    __,
    Placeholders
} from '~/shared/src/utilities/messageCatalog';
import {
    LoadDataParameters,
    LoadMetaDataRowData
} from '~/shared/src/utilities/modules/overview';
import {
    Alignment,
    Visibility
} from '~/shared/src/utilities/modules/form';

import Overview, {
    Overview as OverviewClass,
    AllProps as OverviewAllProps
} from '~/frontend/src/components/base/Overview';
import {Variables} from '~/frontend/src/components/base/Form';

import {getHeightStyle} from '~/frontend/src/components/base/shared/field/valueTypeFields/TextField';
import {MessageResolver} from '~/frontend/src/components/base/shared/field/FieldWrapper';
import {getSubPanelProps} from '~/frontend/src/components/base/page/PageTabPanel/utilities';

import {
    AppDispatch,
    StoreState
} from '~/frontend/src/store';
import origSetParam, {
    SetParamAction
} from '~/frontend/src/actions/panel/common/setParam';
import origSetParams, {
    SetParamsAction
} from '~/frontend/src/actions/panel/common/setParams';


type DefaultProps = {

    /**
     * The CSS class name(s) to add to the wrapper.
     */
    classNames? : string[];

    /**
     * Is he overview disabled?
     */
    disabled? : boolean;

    /**
     * How the view to display as?
     */
    displayMode? : 'card' | 'field';

    /**
     * The number of text rows to be displayed within the overview.
     */
    height? : number;

    /**
     * Should the overview marked if dirty.
     */
    markDirty? : boolean;

    /**
     * The max number of text rows to be displayed within the overview.
     * 0 means unlimited
     */
    maxHeight? : number;

    mode? : 'design' | 'normal';

    /**
     * The styles to style the wrapper with.
     */
    style? : React.CSSProperties;

    /**
     * The alignment of the title for the overview.
     */
    titleAlignment? : Alignment;

    /**
     * The CSS class names to title the label with.
     */
    titleClassNames? : string[];

    /**
     * The styles to style the title with.
     */
    titleStyle? : React.CSSProperties;

    /**
     * The visibility of the title for the overview.
     */
    titleVisibility? : Visibility;

    /**
     * The visibility of the toolbar for the overview.
     */
    toolbarVisibility? : Visibility;

    /**
     * The visibility of the field
     */
    visibility? : Visibility;
};
const defaultProps : Required<DefaultProps> = {
    classNames       : [ 'yn-field-wrapper', 'field' ],
    disabled         : false,
    displayMode      : 'field',
    height           : 0,
    markDirty        : false,
    maxHeight        : 0,
    mode             : 'normal',
    style            : {},
    titleAlignment   : 'left',
    titleClassNames  : [ 'yn-field-label' ],
    titleStyle       : {},
    titleVisibility  : 'visible',
    toolbarVisibility: 'visible',
    visibility       : 'visible'
};

type Props = {

    /**
     * The uniqueId of the panel the component is used in.
     *
     * Mostly used to extract the components state from the global one
     * and to generate unique dom element ids.
     */
    uid : string;

    id? : string;

    name : string;

    viewKey : string;

    dataContext : string;

    /**
     * Additional parameters for the query.
     *
     * NOTE: A mapping between query fields and form contents.
     *
     * USAGE:
     * DataStructure based query:
     *   - key: ${<DataObjectOfQuery.itemKey>.<attributePath>}; value: ${<DataObjectOfForm.itemKey>.<attributePath>}
     * Cypher or SQL based query:
     *   - key: ${ColumnNameAsWrittenInQuery}; value: ${<DataObjectOfForm.itemKey>.<attributePath>}
     */
    additionalParameters? : Required<LoadDataParameters>[ 'parameters' ];

    columns? : string;
    filters? : string;
    fixFilters? : string;
    sorters? : string;

    /**
     * An optional variable that stores placeholders to be used in message templates.
     * These placeholders are utilized to dynamically insert values into a formatted message.
     * The structure and types of the placeholders are defined by the `Placeholders` interface.
     */
    messagePlaceholders? : Placeholders;

    /**
     * A configurable property that optionally specifies the message template string resolver.
     * This resolver is responsible for handling dynamic replacement or formatting of message strings
     * in templates, enabling custom logic for resolving placeholders or variables in messages.
     * It can be particularly useful for internationalization, localization, or dynamic content insertion
     * within message templates.
     *
     * The resolver should conform to the `MessageResolver` structure or interface, which determines
     * how message templates are processed and resolved.
     *
     * If not defined or set to `undefined`, no message resolution will take place.
     */
    messageTemplateStringResolver? : MessageResolver;

    /**
     * The title for the overview.
     */
    title? : string;

    /**
     * PassTrough options
     */
    pt? : {
        root? : React.HTMLAttributes<HTMLDivElement> & { [ key : `data-${string}` ] : string };
    };

    onPropertyChange? : ( propertyTypeName : string, value : AttributeValue | undefined ) => void;

    originalOverviewDefaultProps? : OverviewAllProps['defaultProps'];

    /**
     * Fired on click
     *
     * @param originalEvent
     */
    onTriggerAction : ( originalEvent : any ) => void;

    variables? : Variables;
};

export type ComponentProps = DefaultProps & Props;
export const componentProps = keys<ComponentProps>();

type AllProps = Required<DefaultProps> & Props;


const setParam = origSetParam as <K extends keyof AllProps>( panelKey : string, paramKey : K, paramValue : AllProps[K] ) => SetParamAction;
const setOverviewParams = origSetParams as ( panelKey : string, panelProps : Partial<OverviewAllProps> ) => SetParamsAction;


export type ReferenceType = {
    dirty : () => boolean;
    dirtyDataContexts : () => string[];
};


/**
 * OverviewView is a React functional component that renders an overview panel based on provided
 * properties and application state. It adapts dynamically according to the application's state
 * and given parameters.
 *
 * Functional Description:
 * - Manages the rendering of an "Overview" component when visibility is not set to 'hidden'.
 * - Combines properties passed as `props` with additional state-based "sub-panel" properties.
 * - Sets up default and configurable values for the overview, such as filters, sorters, columns,
 *   and display options based on the context and user preferences.
 * - Acts as a container for initializing, rendering, and managing the lifecycle of the Overview
 *   component while ensuring its integration with Redux store-based state management and dispatching actions.
 *
 * Key Features:
 * - Configurable properties including title, height, styles, toolbar visibility, and more.
 * - Integration with Redux store to fetch relevant configurations (e.g., user session data,
 *   overview-specific properties).
 * - Support for processing templated language-resolved strings with placeholders.
 * - Memoized calculations and dependencies to optimize rendering performance.
 * - Handles dynamic updates for the overview configuration such as managing filters,
 *   column layouts, sorting, and accessibility settings.
 * - Custom callbacks to manage row data changes and reversion events in the overview.
 *
 * Parameters:
 * - props: An object of type `AllProps` containing configuration details for the component.
 *   These include properties for visibility, title, style, columns, filters, on-change handlers,
 *   and Redux state or dispatch-related operations. The passed props are merged dynamically
 *   with state-derived configurations for comprehensive setup.
 *
 * Implementation Notes:
 * - Utilizes React hooks including `useSelector`, `useDispatch`, `useRef`, and `useMemo`
 *   for managing state, memoization, and dispatching actions to Redux.
 * - The internal mechanisms parse and process JSON-based input properties (e.g., filters
 *   and columns), ensuring compatibility with both predefined and dynamically provided data.
 * - The Overview component within the rendering pipeline has an embedded non-card mode
 *   configuration to align with contextual requirements.
 * - Ensures dynamic adjustments to the overview's lifecycle and properties based on
 *   `props`, states, and external triggers while dispatching specific parameters when needed.
 */
const OverviewView = React.forwardRef<ReferenceType, AllProps>( ( props : AllProps, forwardedRef ) => {
    // avoid rendering if hidden
    if ( props.visibility === 'hidden' ) {
        return null;
    }
    else {
        useSelector( ( state : StoreState ) => {
            props = {
                ...props,
                ...getSubPanelProps( props.uid, state )
            };
        } );

        const dispatch : AppDispatch          = useDispatch(),
              {
                  additionalParameters,
                  classNames,
                  columns,
                  dataContext,
                  disabled,
                  displayMode,
                  filters,
                  fixFilters,
                  height,
                  id,
                  //markDirty,
                  maxHeight,
                  messagePlaceholders,
                  messageTemplateStringResolver,
                  mode,
                  name,
                  onPropertyChange,
                  onTriggerAction,
                  originalOverviewDefaultProps,
                  pt,
                  sorters,
                  style,
                  title,
                  titleAlignment,
                  titleClassNames,
                  titleStyle,
                  titleVisibility,
                  toolbarVisibility,
                  uid,
                  variables,
                  viewKey
              }                               = props,
              elementId                       = id ?? `${uid}_overviewView`,
              overviewUid                     = `${uid}_overview`,
              user                            = useSelector( ( state : StoreState ) => state.session?.user ),
              overviewProps                   = useSelector( ( state : StoreState ) => {
                  let retVal : OverviewAllProps | undefined = getSubPanelProps( overviewUid, state ) as OverviewAllProps;

                  if ( Object.keys( retVal ).length === 0 ) {
                      retVal = undefined;
                  }

                  return retVal;
              } ),
              overview                        = useRef<OverviewClass>( null ),
              configLoad                      = useRef<boolean>( false ),
              messageResolver                 = useMemo(
                  () => {
                      if ( !messageTemplateStringResolver ) {
                          return ( messageKey : string ) : string | undefined => messageKey === '' ? undefined : __( messageKey, messagePlaceholders, false );
                      }
                      else {
                          return ( messageKey : string ) => messageKey === '' ? undefined : __( messageTemplateStringResolver( __( messageKey, messagePlaceholders ) ) ?? '', undefined, false );
                      }
                  },
                  [
                      messagePlaceholders,
                      messageTemplateStringResolver
                  ]
              ),
              resolvedTitle                   = useMemo(
                  () => {
                      return title ? messageResolver( title ) : undefined;
                  },
                  [
                      JSON.stringify( title ),
                      messageResolver,
                      user ? user.language : undefined
                  ]
              ),
              heightStyle                     = useMemo(
                  () => {
                      const retVal = getHeightStyle( {}, height, maxHeight, 'hidden', 'left', 'hidden' );

                      retVal.minHeight = '20rem';

                      return retVal;
                  },
                  [
                      height,
                      maxHeight
                  ]
              ),
              setOriginalOverviewDefaultProps = () => {
                  // save Overviews original defaults
                  dispatch( setParam(
                      uid,
                      'originalOverviewDefaultProps',
                      overviewProps?.defaultProps ?? {}
                  ) );
              },
              /**
               * Sets the overview properties for a specific overview instance.
               *
               * This function initializes and adjusts the properties for an overview by combining default property values,
               * user-provided values, and optional fallback values. The resulting properties are then dispatched as part of the
               * overview parameters for further use.
               *
               * Properties being set include:
               * - Columns
               * - Filters
               * - Fix filters
               * - Sorters
               * - Search behavior
               * - Force fit status
               * - Auto layout behavior
               *
               * The function ensures that properties are merged appropriately, taking into account existing default properties
               * and any specified overrides.
               *
               * Requirements:
               * - Ensures parsing of JSON strings where applicable.
               * - Merges user-defined settings with predefined defaults.
               * - Updates and dispatches the overview parameters.
               *
               * Note:
               * - Input sources such as `columns`, `filters`, and `sorters` are parsed from JSON strings if provided.
               * - Falls back to various levels of defaults where certain inputs are not present.
               *
               * Dispatch:
               * Dispatches the updated `propsToSet` object with the specific overview identifier `overviewUid` to
               * the `setOverviewParams` action.
               */
              setOverviewProps                = () => {
                  const defaultPropsToSet                      = {
                            ...overviewProps?.defaultProps
                        },
                        propsToSet : Partial<OverviewAllProps> = {
                            columns   : columns
                                ? JSON.parse( columns )
                                : originalOverviewDefaultProps
                                    ? originalOverviewDefaultProps.columns
                                    : defaultPropsToSet.columns,
                            filters   : filters
                                ? JSON.parse( filters )
                                : originalOverviewDefaultProps
                                    ? originalOverviewDefaultProps.filters
                                    : defaultPropsToSet.filters,
                            fixFilters: fixFilters
                                ? JSON.parse( fixFilters )
                                : [],
                            sorters   : sorters
                                ? JSON.parse( sorters )
                                : originalOverviewDefaultProps
                                    ? originalOverviewDefaultProps.sorters
                                    : defaultPropsToSet.sorters,
                            doSearch  : overviewProps?.search,
                            forceFit  : originalOverviewDefaultProps?.forceFit,
                            autoLayout: originalOverviewDefaultProps?.autoLayout
                        };

                  propsToSet.defaultProps = {
                      ...defaultPropsToSet,
                      columns: propsToSet.columns,
                      filters: propsToSet.filters,
                      sorters: propsToSet.sorters
                  };

                  dispatch( setOverviewParams(
                      overviewUid,
                      propsToSet
                  ) );
              },
              /**
               * The `overview` variable is a memoized React component that renders the `Overview` component. It encapsulates the required properties
               * and configurations for the `Overview` component, dynamically adjusting its behavior based on the surrounding context.
               *
               * Configuration Details:
               * - Key is set dynamically based on the element id.
               * - Accepts several props including `overviewProps`, `uid`, `dataContext`, and `viewKey`.
               * - Utilizes a conditional setup for additional query parameters based on the mode (design or non-design).
               * - Operates in an embedded no-card mode with optional configurability for editability and toolbar visibility.
               * - Handles row data changes and reverts by invoking the provided `onTriggerAction` callback with the appropriate event details.
               * - Provides an `onConfigLoad` function that dispatches configuration parameters to the Redux store while handling configuration states for the overview.
               * - React dependencies for memoization include `additionalParameters`, `configLoad.current`, `dataContext`, `disabled`, `elementId`, `overviewProps`, `overviewUid`, and `viewKey`.
               *
               * This variable ensures efficient re-renders in a React environment by leveraging the `useMemo` hook and only recalculating
               * the component output when its dependencies change.
               */
              overviewEl                      = useMemo(
                  () => {
                      return <Overview
                          key={`${elementId}_overview`}
                          ref={overview}

                          {...{
                              ...overviewProps,

                              uid                      : overviewUid,
                              dataContext              : dataContext,
                              viewKey                  : viewKey,
                              loadDataRoute            : 'overview/loadDataForView',
                              additionalQueryParameters: mode === 'design' ? {
                                      ...additionalParameters,
                                      '__overviewViewKey' : name,
                                      '__designColumns'   : columns,
                                      '__designFilters'   : filters,
                                      '__designFixFilters': fixFilters
                                  }
                                  : {
                                      ...additionalParameters,
                                      '__overviewViewKey': name
                                  },
                              mode                     : 'embeddedNoCard',

                              editable: disabled
                                  ? false
                                  : overviewProps?.editable,

                              showViewAsConfigured: true,
                              toolbarVisibility,

                              treeVariables: variables,

                              onRowDataChange: ( field : string, value : string, rowData : LoadMetaDataRowData, event : React.SyntheticEvent ) => {
                                  onTriggerAction?.( { type: 'rowDataChange', originalEvent: event, field, value, rowData } );
                              },
                              onRowDataRevert: ( field : string, rowData : LoadMetaDataRowData, event : React.SyntheticEvent ) => {
                                  onTriggerAction?.( { type: 'rowDataRevert', originalEvent: event, field, rowData } );
                              },
                              onConfigLoad   : () => {
                                  // avoid direct loading - is re-triggered on setting overview params
                                  dispatch( setOverviewParams(
                                      overviewUid,
                                      {
                                          doSearch: false
                                      }
                                  ) );

                                  configLoad.current = true;
                              }
                          } as OverviewAllProps}

                          //onErrorDataChanged={this.onFormErrorDataChanged}
                      />;
                  },
                  [
                      additionalParameters,
                      configLoad.current,
                      dataContext,
                      disabled,
                      elementId,
                      overviewProps,
                      overviewUid,
                      viewKey
                  ]
              );


        // TODO: column width changes depending on forceFit || autoLayout -> what you see is NOT what you get
        // TODO: non-autoHeight (height = 0) not working (e.g. height = 2)


        useImperativeHandle(
            forwardedRef,
            () => ( {
                dirty            : () => overview.current?.state.isDirty ?? false,
                dirtyDataContexts: () => (
                    overview.current?.state.isDirty ?? false
                        ? [ dataContext ]
                        : []
                )
            } )
        );


        // set overview props after config load
        useEffect(
            () => {
                if ( configLoad.current ) {
                    configLoad.current = false;

                    setOriginalOverviewDefaultProps();
                    setOverviewProps();
                }
            },
            [
                configLoad.current
            ]
        );

        // update overview columns
        useEffect(
            () => {
                if ( overviewProps ) {
                    setOverviewProps();
                }
            },
            [
                columns
            ]
        );

        // update overview filters
        useEffect(
            () => {
                if ( overviewProps ) {
                    setOverviewProps();
                }
            },
            [
                filters
            ]
        );

        // update overview fixFilters
        useEffect(
            () => {
                if ( overviewProps ) {
                    setOverviewProps();
                }
            },
            [
                fixFilters
            ]
        );

        // update overview sorters
        useEffect(
            () => {
                if ( overviewProps ) {
                    setOverviewProps();
                }
            },
            [
                sorters
            ]
        );

        // update columns
        useEffect(
            () => {
                if ( onPropertyChange && originalOverviewDefaultProps ) {
                    const stringifiedOverviewColumns = JSON.stringify( overviewProps?.columns ?? [] );

                    if ( stringifiedOverviewColumns !== JSON.stringify( columns ?? [] ) ) {
                        if ( stringifiedOverviewColumns !== JSON.stringify( originalOverviewDefaultProps.columns ?? [] ) ) {
                            onPropertyChange( 'Columns', stringifiedOverviewColumns );
                        }
                        else if ( columns ) {
                            onPropertyChange( 'Columns', undefined );
                        }
                    }
                }
            },
            [
                overviewProps?.columns
            ]
        );

        // update filters
        useEffect(
            () => {
                if ( onPropertyChange && originalOverviewDefaultProps ) {
                    const stringifiedOverviewFilters = JSON.stringify( overviewProps?.filters ?? [] );

                    if ( stringifiedOverviewFilters !== JSON.stringify( filters ?? [] ) ) {
                        if ( stringifiedOverviewFilters !== JSON.stringify( originalOverviewDefaultProps.filters ?? [] ) ) {
                            onPropertyChange( 'Filters', stringifiedOverviewFilters );
                        }
                        else if ( filters ) {
                            onPropertyChange( 'Filters', undefined );
                        }
                    }
                }
            },
            [
                overviewProps?.filters
            ]
        );

        // update sorters
        useEffect(
            () => {
                if ( onPropertyChange && originalOverviewDefaultProps ) {
                    const stringifiedOverviewSorters = JSON.stringify( overviewProps?.sorters ?? [] );

                    if ( stringifiedOverviewSorters !== JSON.stringify( sorters ?? [] ) ) {
                        if ( stringifiedOverviewSorters !== JSON.stringify( originalOverviewDefaultProps.sorters ?? [] ) ) {
                            onPropertyChange( 'Sorters', stringifiedOverviewSorters );
                        }
                        else if ( sorters ) {
                            onPropertyChange( 'Sorters', undefined );
                        }
                    }
                }
            },
            [
                overviewProps?.sorters
            ]
        );

        return useMemo(
            () => {
                if ( displayMode === 'card' ) {
                    return <div
                        {...pt?.root}
                        id={elementId}
                        key={elementId}
                        className="yn-form-card yn-view-card"
                        style={style}
                    >
                        <Card
                            id={`${elementId}_card`}
                            key={`${elementId}_card`}
                            title={resolvedTitle ? <span style={titleStyle}>{resolvedTitle}</span> : undefined}
                            className={`yn-title-align-${titleAlignment} yn-title-visibility-${titleVisibility}`}
                        >
                            <div
                                className={`yn-view-wrapper yn-view-wrapper-overview yn-view-wrapper-card ${classNames.join( ' ' )}`}
                                style={{ ...heightStyle, ...style }}
                            >
                                <div
                                    id={`${elementId}_border`}
                                    key={`${elementId}_border`}
                                    className="yn-view-wrapper-border"
                                >
                                    {overviewEl}
                                </div>
                            </div>
                        </Card>
                    </div>;
                }
                else {
                    return <div
                        {...pt?.root}
                        id={elementId}
                        key={elementId}
                        className={`yn-view-wrapper yn-view-wrapper-overview yn-form-view ${classNames.join( ' ' )}`}
                        style={{ ...heightStyle, ...style }}
                    >
                        {
                            // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                            ( resolvedTitle || titleVisibility === 'placeholder' ) &&
                            <div className={`yn-form-container-title yn-title-align-${titleAlignment} yn-title-visibility-${titleVisibility} ${titleClassNames.join( ' ' )}`} style={titleStyle}>{resolvedTitle ?? ' '}</div>
                        }
                        <div
                            className="yn-view-wrapper-border"
                        >
                            {overviewEl}
                        </div>
                    </div>;
                }
            },
            [
                classNames,
                displayMode,
                elementId,
                heightStyle,
                overviewEl,
                pt,
                resolvedTitle,
                style,
                title,
                titleAlignment,
                titleClassNames,
                titleStyle,
                titleVisibility
            ]
        );
    }
} );
OverviewView.defaultProps = defaultProps;
OverviewView.displayName = 'OverviewView';

export default OverviewView;
